import { useState } from 'react'
import { RotateCcw, Save } from 'lucide-react'
import { Button, Label, Slider } from '@teach/ui'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  /** Called live on every change so the real cover updates immediately. */
  onChange: (gradient: string) => void
  /** Persist the current gradient into the saved list. */
  onSave: (gradient: string) => void
}

/**
 * Inline builder for a custom two-stop gradient. Rendered inside a Popover
 * (see GradientPresets). Two native color swatches + angle slider; every change
 * calls onChange so the cover preview updates live, "Сохранить градиент" persists.
 */
export const GradientCustomBuilder = ({ onChange, onSave }: Props) => {
  const m = useMessages(messages)
  const [color1, setColor1] = useState('#667eea')
  const [color2, setColor2] = useState('#764ba2')
  const [angle, setAngle] = useState(135)

  const gradientValue = `linear-gradient(${angle}deg, ${color1} 0%, ${color2} 100%)`

  const updateColor1 = (v: string) => {
    setColor1(v)
    onChange(`linear-gradient(${angle}deg, ${v} 0%, ${color2} 100%)`)
  }
  const updateColor2 = (v: string) => {
    setColor2(v)
    onChange(`linear-gradient(${angle}deg, ${color1} 0%, ${v} 100%)`)
  }
  const updateAngle = (a: number) => {
    setAngle(a)
    onChange(`linear-gradient(${a}deg, ${color1} 0%, ${color2} 100%)`)
  }
  const swapColors = () => {
    setColor1(color2)
    setColor2(color1)
    onChange(`linear-gradient(${angle}deg, ${color2} 0%, ${color1} 100%)`)
  }

  return (
    <div className="space-y-4">
      {/* Color pickers */}
      <div className="flex gap-3">
        <div className="flex-1 space-y-1.5">
          <Label className="text-xs text-muted-foreground">{m.color1}</Label>
          <div className="flex items-center gap-2">
            <label
              className="w-8 h-8 rounded-lg cursor-pointer block shrink-0"
              style={{ backgroundColor: color1 }}
            >
              <input
                type="color"
                value={color1}
                onChange={(e) => updateColor1(e.target.value)}
                className="sr-only"
              />
            </label>
            <span className="text-xs text-muted-foreground font-mono">{color1}</span>
          </div>
        </div>
        <div className="flex-1 space-y-1.5">
          <Label className="text-xs text-muted-foreground">{m.color2}</Label>
          <div className="flex items-center gap-2">
            <label
              className="w-8 h-8 rounded-lg cursor-pointer block shrink-0"
              style={{ backgroundColor: color2 }}
            >
              <input
                type="color"
                value={color2}
                onChange={(e) => updateColor2(e.target.value)}
                className="sr-only"
              />
            </label>
            <span className="text-xs text-muted-foreground font-mono">{color2}</span>
          </div>
        </div>
      </div>

      {/* Swap colors */}
      <div className="flex justify-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={swapColors}
          className="text-xs gap-1.5 text-muted-foreground hover:text-foreground"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          {m.swapColors}
        </Button>
      </div>

      {/* Angle */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">{m.angle(angle)}</Label>
        <Slider
          min={0}
          max={360}
          step={1}
          value={[angle]}
          onValueChange={([a]) => updateAngle(a)}
        />
      </div>

      {/* Save */}
      <Button onClick={() => onSave(gradientValue)} className="w-full">
        <Save className="w-3.5 h-3.5" />
        {m.saveGradient}
      </Button>
    </div>
  )
}
